import { useState, type ReactNode, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, CheckCircle, AlertCircle } from 'lucide-react'
import { subscribeToNewsletter } from '../../services/newsletter'
import { useAuth } from '../../store/hooks/useAuth'
import { LoginPage } from '../Auth/LoginPage'
import { LanguageSwitcher } from '../LanguageSwitcher'
import landingStyles from '../Landing/Landing.module.css'
import styles from './MarketingLayout.module.css'

interface MarketingLayoutProps {
  children: ReactNode
}

export function MarketingLayout({ children }: MarketingLayoutProps): ReactElement {
  const { t } = useTranslation('landing')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const [showLogin, setShowLogin] = useState(false)
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')

  function navigateToSessions() {
    window.location.href = '/sessions'
  }

  async function handleNewsletterSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    if (!email.trim()) {
      setSubmitStatus('error')
      setSubmitMessage(t('enterEmailError'))
      return
    }
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setSubmitMessage('')
    try {
      const response = await subscribeToNewsletter({
        email: email.trim(),
        source: 'marketing_page',
        tags: ['early_access', 'marketing'],
      })
      setSubmitStatus('success')
      setSubmitMessage(response.message || t('subscribeSuccess'))
      setEmail('')
      setTimeout(() => {
        setSubmitStatus('idle')
        setSubmitMessage('')
      }, 5000)
    } catch (error) {
      console.error('Newsletter subscription error:', error)
      setSubmitStatus('error')
      setSubmitMessage(error instanceof Error ? error.message : t('subscribeFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`theme-light ${landingStyles.root} ${styles.root}`}>
      {/* Header — matches landing page */}
      <header className={landingStyles.siteHeader}>
        <div className={landingStyles.maxWidth}>
          <div className={landingStyles.siteHeaderInner}>
            <a href="/" className={landingStyles.brandMark}>
              <span className={landingStyles.brandDot} aria-hidden="true" />
            </a>

            <nav className={landingStyles.heroNav}>
              <a className={landingStyles.minLink} href="/blog">
                {tc('blog')}
              </a>
              <button className={landingStyles.minLink} onClick={navigateToSessions}>
                {tc('explore')}
              </button>
            </nav>

            <div className={landingStyles.heroActions}>
              <button
                className={landingStyles.minLink}
                onClick={() => {
                  if (user) {
                    navigateToSessions()
                  } else {
                    setShowLogin(true)
                  }
                }}
              >
                <span className={landingStyles.signUpLink}>{t('auth:joinTheBeta')}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        {children}
      </main>

      {/* Footer — matches landing page */}
      <footer className={landingStyles.footer}>
        <div className={landingStyles.maxWidth}>
          <div className={landingStyles.footerMain}>
            <div className={landingStyles.footerMiddle}>
              <div className={landingStyles.footerNewsletter}>
                <div className={`${landingStyles.footerLabel} ${styles.footerLabelOffset}`}>
                  {t('stayInTheLoop')}
                </div>
                <form onSubmit={handleNewsletterSubmit} className={landingStyles.emailInputWrapper}>
                  <input
                    type="email"
                    placeholder={t('enterYourEmail')}
                    className={landingStyles.emailInput}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    aria-label={t('auth:emailAddress')}
                  />
                  <button
                    type="submit"
                    className={landingStyles.subscribeBtn}
                    disabled={isSubmitting || submitStatus === 'success'}
                    aria-label={isSubmitting ? t('subscribing') : t('subscribe')}
                  >
                    {submitStatus === 'success' ? (
                      <CheckCircle size={20} />
                    ) : submitStatus === 'error' ? (
                      <AlertCircle size={20} />
                    ) : (
                      <ArrowRight size={20} />
                    )}
                  </button>
                </form>
                {submitMessage && (
                  <div
                    className={`${landingStyles.submitMessage} ${
                      submitStatus === 'success'
                        ? landingStyles.submitMessageSuccess
                        : submitStatus === 'error'
                          ? landingStyles.submitMessageError
                          : ''
                    } ${styles.submitMessageOffset}`}
                  >
                    {submitMessage}
                  </div>
                )}
              </div>

              <div className={landingStyles.footerLinksGroup}>
                <div className={landingStyles.footerInfo}>
                  <div className={landingStyles.footerLabel}>{tc('product')}</div>
                  <nav className={landingStyles.footerNavColumn}>
                    <a href="/features/workflow" className={landingStyles.footerLink}>{tc('features')}</a>
                    <a href="/blog" className={landingStyles.footerLink}>{tc('blog')}</a>
                  </nav>
                </div>

                <div className={landingStyles.footerInfo}>
                  <div className={landingStyles.footerLabel}>{t('info')}</div>
                  <nav className={landingStyles.footerNavColumn}>
                    <a href="/privacy" className={landingStyles.footerLink}>{tc('privacyPolicy')}</a>
                    <a href="/terms" className={landingStyles.footerLink}>{tc('termsOfService')}</a>
                  </nav>
                </div>

                <div className={landingStyles.footerInfo}>
                  <div className={landingStyles.footerLabel}>{tc('language')}</div>
                  <LanguageSwitcher variant="compact" />
                </div>
              </div>
            </div>

            <div className={landingStyles.footerBrandBig}>
              {t('footerBrand')}
            </div>
          </div>
        </div>
      </footer>

      {showLogin && (
        <LoginPage onClose={() => setShowLogin(false)} />
      )}
    </div>
  )
}
